import customerRideApi from 'api/customerRideApi';
import React, { useEffect, useRef, useState } from 'react';
import { rideErrorMessage } from '../rideUtils';
import RideAppIcon from './RideAppIcon';
import RidePortal from './RidePortal';
import {
  RIDE_LOCATION_MIN_QUERY_LENGTH,
  RIDE_LOCATION_SEARCH_DEBOUNCE_MS,
} from '../rideLocationSearchPolicy';

const labelOf = (place) => place?.display || place?.address || '';
// Code 06 is the pilot's former Nam Định end. Keep its API code untouched,
// but present the route name customers actually use in this web portal.
const provinceLabel = (province) => String(province?.code) === '06' ? 'Tỉnh Nam Định' : province?.name;

// Desktop counterpart of RidePointPickerPage: address search is primary;
// province/commune metadata is deliberately not required for a free pin.
const RidePlaceSearchSheet = ({ currentLocation, target, value, onClose, onSelect, onOpenMap }) => {
  const [query, setQuery] = useState(value?.address || '');
  const [provinces, setProvinces] = useState([]);
  const [provinceCode, setProvinceCode] = useState('');
  const [districts, setDistricts] = useState([]);
  const [districtCode, setDistrictCode] = useState('');
  const [wards, setWards] = useState([]);
  const [wardCode, setWardCode] = useState('');
  const [detailAddress, setDetailAddress] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);
  const requestRef = useRef(0);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    let active = true;
    customerRideApi.getProvinces()
      .then((response) => { if (active) setProvinces(response?.provinces || []); })
      .catch(() => { /* Address search remains available if catalog is unavailable. */ });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!provinceCode) { setDistricts([]); return undefined; }
    let active = true;
    customerRideApi.getDistricts(provinceCode)
      .then((response) => { if (active) setDistricts(response?.districts || []); })
      .catch((error) => { if (active) setMessage(rideErrorMessage(error, 'Không thể tải Quận/Huyện.')); });
    return () => { active = false; };
  }, [provinceCode]);

  useEffect(() => {
    if (!provinceCode || !districtCode) { setWards([]); return undefined; }
    let active = true;
    customerRideApi.getWards(provinceCode, districtCode)
      .then((response) => { if (active) setWards(response?.wards || []); })
      .catch((error) => { if (active) setMessage(rideErrorMessage(error, 'Không thể tải Phường/Xã.')); });
    return () => { active = false; };
  }, [districtCode, provinceCode]);

  useEffect(() => {
    const text = query.trim();
    if (text.length < RIDE_LOCATION_MIN_QUERY_LENGTH || text === value?.address) { setSuggestions([]); return undefined; }
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const timer = window.setTimeout(async () => {
      setLoading(true); setMessage('');
      try {
        const response = await customerRideApi.autocomplete({
          text,
          ...(currentLocation ? { lat: currentLocation.latitude, lng: currentLocation.longitude } : {}),
        });
        if (requestRef.current === requestId) setSuggestions((response?.suggestions || []).slice(0, 8));
      } catch (error) {
        if (requestRef.current === requestId) setMessage(rideErrorMessage(error, 'Không thể tìm địa điểm lúc này.'));
      } finally { if (requestRef.current === requestId) setLoading(false); }
    }, RIDE_LOCATION_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [currentLocation, query, value?.address]);

  const chooseSuggestion = async (suggestion) => {
    if (!suggestion?.ref_id) return;
    setLoading(true); setMessage('');
    try {
      const response = await customerRideApi.resolvePlace(suggestion.ref_id);
      const place = response?.place || {};
      const latitude = Number(place.lat);
      const longitude = Number(place.lng);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) throw new Error('Invalid location');
      const selectedProvince = provinces.find((province) => String(province.code) === String(provinceCode));
      onSelect({
        address: labelOf(suggestion) || place.display || place.address,
        latitude,
        longitude,
        // This is the legacy 3-level address chooser. Its codes are used only
        // to compose the search; ride payload deliberately uses a null pair.
        provinceCode: null,
        communeCode: null,
      });
    } catch (error) {
      setMessage(rideErrorMessage(error, 'Không thể xác định tọa độ địa điểm này.'));
    } finally { setLoading(false); }
  };

  const chooseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMessage('Trình duyệt này không hỗ trợ xác định vị trí.');
      return;
    }
    setLocating(true); setMessage('');
    let settled = false;
    const finish = (callback) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(guard);
      callback();
    };
    // Match the app's 12s high-accuracy request, with an explicit guard for
    // desktop browsers that occasionally do not fire a geolocation callback.
    const guard = window.setTimeout(() => finish(() => {
      setLocating(false);
      setMessage('Không thể xác định vị trí trong 12 giây. Hãy kiểm tra quyền Vị trí rồi thử lại.');
    }), 12500);
    navigator.geolocation.getCurrentPosition(async (position) => finish(async () => {
      const latitude = position.coords.latitude;
      const longitude = position.coords.longitude;
      const base = {
        address: 'Khu vực hiện tại', latitude, longitude,
        provinceCode: null, communeCode: null,
        lowAccuracy: Number(position.coords.accuracy) > 100,
      };
      try {
        const response = await customerRideApi.reverseGeocode({ lat: latitude, lng: longitude });
        const place = response?.results?.[0] || response?.data?.results?.[0] || {};
        setLocating(false);
        onSelect({ ...base, address: place.display || place.address || base.address });
      } catch (_) {
        setLocating(false);
        onSelect(base);
      }
    }), (error) => finish(() => {
      setLocating(false);
      if (error?.code === 1) setMessage('Bạn chưa cho phép truy cập vị trí. Hãy bật quyền Vị trí cho trang này rồi thử lại.');
      else if (error?.code === 2) setMessage('Thiết bị hiện không cung cấp được vị trí. Hãy bật Dịch vụ định vị rồi thử lại.');
      else setMessage('Không thể xác định vị trí trong 12 giây. Hãy kiểm tra quyền Vị trí rồi thử lại.');
    }), { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 });
  };

  const updateAdministrativeQuery = (nextDetail = detailAddress, nextDistrictCode = districtCode, nextWardCode = wardCode) => {
    const province = provinces.find((item) => String(item.code) === String(provinceCode));
    const ward = wards.find((item) => String(item.code) === String(nextWardCode));
    const selectedDistrict = districts.find((item) => String(item.code) === String(nextDistrictCode));
    const parts = [nextDetail, ward?.name, selectedDistrict?.name, provinceLabel(province)].filter(Boolean);
    setQuery(parts.join(', '));
  };

  const title = target === 'PICKUP' ? 'Chọn điểm đón' : 'Chọn điểm đến';
  return (
    <RidePortal>
      <div className="ride-place-sheet-backdrop" role="presentation" onMouseDown={onClose}>
        <section className="ride-place-sheet" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(event) => event.stopPropagation()}>
          <div className="ride-place-sheet__head"><div><span>{target === 'PICKUP' ? 'Điểm đón' : 'Điểm đến'}</span><h3>{title}</h3></div><button type="button" aria-label="Đóng" onClick={onClose}><RideAppIcon name="close" /></button></div>
          <div className="ride-place-sheet__search"><RideAppIcon name="search" /><input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Tìm địa điểm, địa chỉ, khu vực..." />{loading && <RideAppIcon name="time" className="ride-icon-spin" />}</div>
      <div className="ride-place-sheet__admin" aria-label="Chọn địa chỉ hành chính">
            <div className="ride-place-sheet__admin-grid">
              <select value={provinceCode} onChange={(event) => { setProvinceCode(event.target.value); setDistrictCode(''); setWardCode(''); setQuery(''); }}><option value="">Chọn Tỉnh/Thành</option>{provinces.map((province) => <option key={province.code} value={province.code}>{provinceLabel(province)}</option>)}</select>
              <select value={districtCode} disabled={!provinceCode} onChange={(event) => { setDistrictCode(event.target.value); setWardCode(''); setQuery(''); }}><option value="">Chọn Quận/Huyện</option>{districts.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}</select>
              <select value={wardCode} disabled={!districtCode} onChange={(event) => { setWardCode(event.target.value); updateAdministrativeQuery(detailAddress, districtCode, event.target.value); }}><option value="">Chọn Phường/Xã</option>{wards.map((ward) => <option key={ward.code} value={ward.code}>{ward.name}</option>)}</select>
              <input value={detailAddress} disabled={!wardCode} onChange={(event) => { setDetailAddress(event.target.value); updateAdministrativeQuery(event.target.value); }} placeholder={wardCode ? 'Số nhà, tên đường, tòa nhà...' : 'Chọn Phường/Xã trước'} />
            </div>
      </div>
          <div className="ride-place-sheet__quick-actions">
            {target === 'PICKUP' && <button type="button" disabled={locating} onClick={chooseCurrentLocation}><RideAppIcon name={locating ? 'time' : 'locate'} className={locating ? 'ride-icon-spin' : ''} /><span>Dùng vị trí hiện tại</span><RideAppIcon name="chevronRight" /></button>}
            <button type="button" onClick={() => onOpenMap?.(target)}><RideAppIcon name="map" /><span>Chọn trên bản đồ</span><RideAppIcon name="chevronRight" /></button>
          </div>
          <div className="ride-place-sheet__results">
            {query.trim().length < RIDE_LOCATION_MIN_QUERY_LENGTH && <p>Nhập ít nhất 3 ký tự để tìm địa điểm.</p>}
            {query.trim().length >= RIDE_LOCATION_MIN_QUERY_LENGTH && !loading && suggestions.length === 0 && !message && <p>Không tìm thấy địa điểm phù hợp.</p>}
            {suggestions.map((suggestion) => {
              const parts = labelOf(suggestion).split(',');
              const title = parts.shift()?.trim() || labelOf(suggestion);
              const address = parts.join(',').trim();
              return <button type="button" key={suggestion.ref_id} onClick={() => chooseSuggestion(suggestion)}><RideAppIcon name="destination" /><span><b>{title}</b>{address && <small>{address}</small>}</span><RideAppIcon name="chevronRight" /></button>;
            })}
            {message && <p className="ride-place-sheet__error">{message}</p>}
          </div>
        </section>
      </div>
    </RidePortal>
  );
};

export default RidePlaceSearchSheet;
